import React from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Info,
  Pencil,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../LoadingSpinner';
import { EmployeeTimeEntryEditor } from '../EmployeeTimeEntryEditor';
import { EmployeePayrollToolbar } from './EmployeePayrollToolbar';
import type { EmployeeOverviewFilterOption } from '../overview/EmployeeOverviewMultiSelect';
import {
  formatPeriodLabel,
  formatPeriodMonthTitle,
} from '../../../utils/employeeRoutes';
import {
  buildExportParams,
  canEditIssue,
  formatEuro,
  formatEuroInt,
  formatSignedEuro,
  formatHours,
  formatHoursWithUnit,
  formatIssueDate,
  isEmployeeIncluded,
  issueRowKey,
  issueTypeLabel,
  normalizeExportCandidate,
  normalizePayrollEmployeeSummary,
  buildPayrollBruttoTooltip,
  buildPayrollZulageTooltip,
  layoutPayrollEditPopover,
  type EditTarget,
  type ExportCandidate,
  type PayrollEmployeeSummary,
  type PayrollExportIssue,
} from './payrollExportShared';
import { ENTRY_TYPE_LABEL } from '../employeeTimeEntryConstants';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';

const ISSUES_INITIAL_VISIBLE = 12;
const ISSUES_PAGE_SIZE = 50;

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function avatarClass(colorIdx: number | undefined | null) {
  const idx = Number.isFinite(Number(colorIdx)) ? Number(colorIdx) : 0;
  return AVATAR_COLORS[((idx % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length];
}

type EmployeePayrollTabProps = {
  from: string;
  to: string;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
  onOpenEmployeeTimeTracking?: (employeeId: string) => void;
  onTimeEntrySaved?: () => void;
};

function PayrollInfoTooltipLines({
  breakdown,
  ariaLabel,
}: {
  breakdown: ReturnType<typeof buildPayrollBruttoTooltip>;
  ariaLabel: string;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{
    top: number;
    left: number;
    placement: 'above' | 'below';
  } | null>(null);

  const updateTooltipPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;
    const minSpaceAbove = 200;
    const placement: 'above' | 'below' = rect.top >= minSpaceAbove ? 'above' : 'below';
    setTooltipPos({
      top: placement === 'above' ? rect.top - gap : rect.bottom + gap,
      left: rect.right,
      placement,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updateTooltipPosition();
    const onReposition = () => updateTooltipPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updateTooltipPosition]);

  if (!breakdown) return null;

  const tooltipPanel =
    open && tooltipPos ? (
      <div
        role="tooltip"
        className="fixed z-[250] w-max max-w-[min(320px,calc(100vw-24px))] rounded-lg border border-app-border bg-app-surface px-3 py-2 text-left shadow-lg pointer-events-none"
        style={{
          left: tooltipPos.left,
          top: tooltipPos.top,
          transform:
            tooltipPos.placement === 'above' ? 'translate(-100%, -100%)' : 'translateX(-100%)',
        }}
      >
        <div className="space-y-1">
          {breakdown.map((line, idx) => (
            <p
              key={`${line.kind}-${idx}`}
              className={
                line.kind === 'heading'
                  ? 'text-xs font-medium text-app-text-muted pt-1 first:pt-0'
                  : line.kind === 'total'
                    ? 'text-xs font-semibold text-app-text pt-1.5 mt-0.5 border-t border-app-border'
                    : line.kind === 'calc'
                      ? 'text-xs text-app-text'
                      : line.kind === 'row'
                        ? 'text-xs font-medium text-app-text'
                        : 'text-xs text-app-text-muted pl-2'
              }
            >
              {line.text}
            </p>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          updateTooltipPosition();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          updateTooltipPosition();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <Info className="h-3 w-3" />
      </button>
      {typeof document !== 'undefined' && tooltipPanel ? createPortal(tooltipPanel, document.body) : null}
    </>
  );
}

function PayrollBruttoAmount({ emp }: { emp: PayrollEmployeeSummary }) {
  const breakdown = buildPayrollBruttoTooltip(emp);

  return (
    <span className="inline-flex items-center justify-end gap-1">
      <span className="text-sm font-semibold tabular-nums text-app-text">
        {formatEuro(emp.forecasted_gross_pay)}
      </span>
      {breakdown ? (
        <PayrollInfoTooltipLines breakdown={breakdown} ariaLabel="Berechnung Brutto-Forecast" />
      ) : null}
    </span>
  );
}

function PayrollZulageAmount({ emp }: { emp: PayrollEmployeeSummary }) {
  const breakdown = buildPayrollZulageTooltip(emp);

  return (
    <span className="inline-flex items-center justify-end gap-1">
      <span className="tabular-nums text-app-text-muted">{formatEuro(emp.allowance_pay)}</span>
      {breakdown ? (
        <PayrollInfoTooltipLines breakdown={breakdown} ariaLabel="Aufschlüsselung Zulagen" />
      ) : null}
    </span>
  );
}

function KpiTile({
  label,
  value,
  unit,
  accentClassName,
}: {
  label: string;
  value: string;
  unit?: string;
  accentClassName?: string;
}) {
  return (
    <div className="bg-app-surface border border-app-border rounded-lg px-4 py-3.5 min-w-0">
      <p className="text-xs font-medium text-app-text-muted truncate">{label}</p>
      <p className="mt-1.5 flex items-baseline gap-1">
        <span className={`text-xl font-semibold tabular-nums truncate ${accentClassName ?? 'text-app-text'}`}>
          {value}
        </span>
        {unit ? <span className="text-xs font-medium text-app-text-muted">{unit}</span> : null}
      </p>
    </div>
  );
}

function PayrollSortableHeader({
  label,
  active,
  dir,
  align,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  align: 'left' | 'right';
  onSort: () => void;
}) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-2.5 py-2.5 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={onSort}
        className={`group/sort inline-flex max-w-full items-center gap-1 text-xs font-medium whitespace-nowrap transition-colors ${
          active ? 'text-app-text' : 'text-app-text-muted hover:text-app-text'
        } ${align === 'right' ? 'ml-auto justify-end' : ''}`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

export function EmployeePayrollTab({
  from,
  to,
  onShift,
  onApplyCustomPeriod,
  onOpenEmployeeTimeTracking,
  onTimeEntrySaved,
}: EmployeePayrollTabProps) {
  const [busy, setBusy] = React.useState(false);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [issues, setIssues] = React.useState<PayrollExportIssue[]>([]);
  const [issuesOpen, setIssuesOpen] = React.useState(true);
  const [employeeSummaries, setEmployeeSummaries] = React.useState<PayrollEmployeeSummary[]>([]);
  const [canExport, setCanExport] = React.useState(false);
  const [exportAvailability, setExportAvailability] = React.useState({
    calendar: false,
    bookings: false,
    loans: false,
    masterData: false,
  });
  const [masterDataChangeCount, setMasterDataChangeCount] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [editTarget, setEditTarget] = React.useState<EditTarget | null>(null);
  const [candidates, setCandidates] = React.useState<ExportCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = React.useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<string[]>([]);
  const [tableSearch, setTableSearch] = React.useState('');
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'brutto', dir: 'desc' });
  const [issuesPage, setIssuesPage] = React.useState(0);
  const [issuesExpanded, setIssuesExpanded] = React.useState(false);
  const editPopoverRef = React.useRef<HTMLDivElement>(null);

  const candidateIds = React.useMemo(() => candidates.map((c) => c.id), [candidates]);

  const filterEmployees: EmployeeOverviewFilterOption[] = React.useMemo(
    () =>
      candidates.map((c) => ({
        id: c.id,
        name: c.name,
        color_idx: c.color_idx ?? 0,
        compensation_type: c.compensation_type === 'salary' ? 'salary' : 'hourly',
      })),
    [candidates],
  );

  const loadCandidates = React.useCallback(async () => {
    if (!from || !to) {
      setCandidates([]);
      setSelectedEmployeeIds([]);
      return;
    }
    setCandidatesLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      const res = await fetch(`/api/employees/payroll-export/candidates?${params}`);
      const json = (await res.json()) as { employees?: ExportCandidate[]; error?: string };
      if (!res.ok) throw new Error(json.error || 'Mitarbeiter konnten nicht geladen werden.');
      const list = Array.isArray(json.employees) ? json.employees : [];
      setCandidates(list);
      setSelectedEmployeeIds((prev) => {
        if (prev.length === 0) return [];
        const next = prev.filter((id) => list.some((e) => e.id === id));
        return next;
      });
    } catch (e) {
      setCandidates([]);
      setSelectedEmployeeIds([]);
      setError(e instanceof Error ? e.message : 'Mitarbeiter konnten nicht geladen werden.');
    } finally {
      setCandidatesLoading(false);
    }
  }, [from, to]);

  const runPreview = React.useCallback(async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      if (!from || !to) {
        setIssues([]);
        setEmployeeSummaries([]);
        setCanExport(false);
        setExportAvailability({ calendar: false, bookings: false, loans: false, masterData: false });
        setMasterDataChangeCount(0);
        return;
      }

      const params = buildExportParams(from, to, selectedEmployeeIds, candidateIds);
      const res = await fetch(`/api/employees/payroll-export/validate?${params}`);
      const json = (await res.json()) as {
        issues?: PayrollExportIssue[];
        employees?: PayrollEmployeeSummary[];
        canExport?: boolean;
        canExportCalendar?: boolean;
        canExportBookings?: boolean;
        canExportLoans?: boolean;
        masterDataChangeCount?: number;
        totalForecastedGrossPay?: number;
        error?: string;
      };
      if (!res.ok) throw new Error(json.error || 'Vorschau konnte nicht geladen werden.');
      const foundIssues = Array.isArray(json.issues) ? json.issues : [];
      setIssues(foundIssues);
      setIssuesOpen(foundIssues.length > 0);
      setEmployeeSummaries(
        Array.isArray(json.employees) ? json.employees.map((e) => normalizePayrollEmployeeSummary(e)) : [],
      );
      setCanExport(Boolean(json.canExport));
      const changeCount = Math.max(0, Math.floor(Number(json.masterDataChangeCount ?? 0)));
      setMasterDataChangeCount(changeCount);
      setExportAvailability({
        calendar: Boolean(json.canExportCalendar),
        bookings: Boolean(json.canExportBookings),
        loans: Boolean(json.canExportLoans),
        masterData: changeCount > 0,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen.');
    } finally {
      setPreviewLoading(false);
    }
  }, [from, to, selectedEmployeeIds, candidateIds]);

  React.useEffect(() => {
    void loadCandidates();
  }, [loadCandidates]);

  React.useEffect(() => {
    if (!from || !to || candidatesLoading) return;
    const t = window.setTimeout(() => {
      void runPreview();
    }, 300);
    return () => window.clearTimeout(t);
  }, [from, to, selectedEmployeeIds, candidatesLoading, runPreview]);

  const downloadPayrollCsv = async (endpoint: string, fallbackFilename: string) => {
    const params = buildExportParams(from, to, selectedEmployeeIds, candidateIds);
    const exportRes = await fetch(`${endpoint}?${params}`);
    if (!exportRes.ok) {
      const errJson = (await exportRes.json().catch(() => ({}))) as { issues?: PayrollExportIssue[]; error?: string };
      if (Array.isArray(errJson.issues) && errJson.issues.length > 0) {
        setIssues(errJson.issues);
        setIssuesOpen(true);
        setCanExport(false);
        return;
      }
      throw new Error(errJson.error || 'Export fehlgeschlagen.');
    }
    const blob = await exportRes.blob();
    const cd = exportRes.headers.get('content-disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/i);
    const filename = m?.[1] || fallbackFilename;
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  const runCsvExport = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!canExport || !exportAvailability.calendar) {
        await runPreview();
        return;
      }
      await downloadPayrollCsv('/api/employees/payroll-export', `lohnexport_${from}_${to}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const runBookingsCsvExport = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!canExport || !exportAvailability.bookings) {
        await runPreview();
        return;
      }
      await downloadPayrollCsv('/api/employees/payroll-bookings-export', `lohnbuchungen_${from}_${to}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const runLoanBookingsCsvExport = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!canExport || !exportAvailability.loans) {
        await runPreview();
        return;
      }
      await downloadPayrollCsv('/api/employees/payroll-loan-bookings-export', `darlehen_${from}_${to}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  // Stammdatenänderungen: unabhängig von den Zeiteintrags-Issues (kein canExport-Gate).
  const runMasterDataChangesExport = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!exportAvailability.masterData) {
        await runPreview();
        return;
      }
      await downloadPayrollCsv(
        '/api/employees/payroll-master-data-changes-export',
        `stammdatenaenderungen_${from}_${to}.csv`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const visibleSummaries = React.useMemo(
    () =>
      employeeSummaries.filter((emp) =>
        isEmployeeIncluded(emp.id, selectedEmployeeIds, candidateIds),
      ),
    [employeeSummaries, selectedEmployeeIds, candidateIds],
  );

  const filteredIssues = React.useMemo(() => {
    if (selectedEmployeeIds.length === 0 || selectedEmployeeIds.length >= candidateIds.length) {
      return issues;
    }
    const allowed = new Set(selectedEmployeeIds);
    return issues.filter((i) => !i.employeeId || allowed.has(i.employeeId));
  }, [issues, selectedEmployeeIds, candidateIds.length]);

  const issuesPageCount = Math.max(1, Math.ceil(filteredIssues.length / ISSUES_PAGE_SIZE));
  const safeIssuesPage = Math.min(issuesPage, issuesPageCount - 1);
  const pageStart = safeIssuesPage * ISSUES_PAGE_SIZE;
  const pageIssues = filteredIssues.slice(pageStart, pageStart + ISSUES_PAGE_SIZE);
  const visibleIssues = issuesExpanded ? pageIssues : pageIssues.slice(0, ISSUES_INITIAL_VISIBLE);
  const hiddenOnPage = Math.max(0, pageIssues.length - visibleIssues.length);

  React.useEffect(() => {
    setIssuesPage(0);
    setIssuesExpanded(false);
  }, [issues, from, to, selectedEmployeeIds]);

  React.useEffect(() => {
    if (issuesPage > issuesPageCount - 1) {
      setIssuesPage(Math.max(0, issuesPageCount - 1));
    }
  }, [issuesPage, issuesPageCount]);

  const goIssuesPage = (next: number) => {
    setIssuesPage(Math.max(0, Math.min(issuesPageCount - 1, next)));
    setIssuesExpanded(false);
  };

  const filteredBrutto = React.useMemo(
    () => visibleSummaries.reduce((s, e) => s + e.forecasted_gross_pay, 0),
    [visibleSummaries],
  );

  const employeeCountLabel =
    selectedEmployeeIds.length === 0 || selectedEmployeeIds.length >= candidateIds.length
      ? visibleSummaries.length
      : selectedEmployeeIds.length;

  // Die Tabelle ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const setTableScrollNode = useHorizontalWheelScroll<HTMLDivElement>();

  const sortedRows = React.useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const rows = visibleSummaries.filter((e) => !q || e.name.toLowerCase().includes(q));
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      switch (sort.key) {
        case 'name':
          return a.name.localeCompare(b.name, 'de') * dir;
        case 'role':
          return (a.position || '').localeCompare(b.position || '', 'de') * dir;
        case 'rate':
          return (a.hourly_wage - b.hourly_wage) * dir;
        case 'reg':
          return (a.net_work_hours - b.net_work_hours) * dir;
        case 'ot':
          return (a.hours_by_type.overtime_comp - b.hours_by_type.overtime_comp) * dir;
        case 'vac':
          return (a.hours_by_type.vacation - b.hours_by_type.vacation) * dir;
        case 'holiday':
          return (a.hours_by_type.public_holiday - b.hours_by_type.public_holiday) * dir;
        case 'sick':
          return (a.hours_by_type.sickness - b.hours_by_type.sickness) * dir;
        case 'zulage':
          return (a.allowance_pay - b.allowance_pay) * dir;
        case 'adjustments':
          return (a.payroll_adjustment_net - b.payroll_adjustment_net) * dir;
        case 'brutto':
          return (a.forecasted_gross_pay - b.forecasted_gross_pay) * dir;
        default:
          return 0;
      }
    });
    return rows;
  }, [visibleSummaries, tableSearch, sort]);

  const footTotals = React.useMemo(() => {
    let reg = 0;
    let ot = 0;
    let vac = 0;
    let holiday = 0;
    let sick = 0;
    let zulage = 0;
    let adjustments = 0;
    let brutto = 0;
    for (const e of sortedRows) {
      reg += e.net_work_hours;
      ot += e.hours_by_type.overtime_comp;
      vac += e.hours_by_type.vacation;
      holiday += e.hours_by_type.public_holiday;
      sick += e.hours_by_type.sickness;
      zulage += e.allowance_pay;
      adjustments += e.payroll_adjustment_net;
      brutto += e.forecasted_gross_pay;
    }
    return { reg, ot, vac, holiday, sick, zulage, adjustments, brutto };
  }, [sortedRows]);

  const issuesByEmployee = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const i of filteredIssues) {
      if (!i.employeeId) continue;
      map.set(i.employeeId, (map.get(i.employeeId) ?? 0) + 1);
    }
    return map;
  }, [filteredIssues]);

  const selectedEmployee = selectedRowId
    ? visibleSummaries.find((e) => e.id === selectedRowId) ?? null
    : null;

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  };

  React.useEffect(() => {
    if (!editTarget) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditTarget(null);
    };
    const onScroll = (e: Event) => {
      const root = editPopoverRef.current;
      const target = e.target;
      if (root && target instanceof Node && root.contains(target)) return;
      setEditTarget(null);
    };
    const onPointerDown = (e: MouseEvent) => {
      const root = editPopoverRef.current;
      const target = e.target;
      if (root && target instanceof Node && root.contains(target)) return;
      setEditTarget(null);
    };
    const onResize = () => setEditTarget(null);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    const attachOutsideClick = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown, true);
    }, 0);
    return () => {
      clearTimeout(attachOutsideClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [editTarget]);

  const editPopoverLayout = React.useMemo(
    () => (editTarget ? layoutPayrollEditPopover(editTarget.anchor) : null),
    [editTarget],
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <EmployeePayrollToolbar
        from={from}
        to={to}
        onShift={onShift}
        onApplyCustomPeriod={onApplyCustomPeriod}
        filterEmployees={filterEmployees}
        selectedEmployeeIds={selectedEmployeeIds}
        onSelectedEmployeeIdsChange={setSelectedEmployeeIds}
        canExport={canExport && filteredIssues.length === 0}
        exportAvailability={exportAvailability}
        exportBusy={busy}
        previewLoading={previewLoading}
        onExportCalendar={() => void runCsvExport()}
        onExportBookings={() => void runBookingsCsvExport()}
        onExportLoans={() => void runLoanBookingsCsvExport()}
        onExportMasterData={() => void runMasterDataChangesExport()}
      />

      <p className="shrink-0 px-6 py-2 text-xs text-app-text-subtle bg-app-surface border-b border-app-border">
        Kalenderexport: Zeiteinträge für Agenda. Lohnbuchungen: Zulage, VMA, ÜN-Pauschale und Be-/Abzüge (ohne Darlehen).
        Darlehensauszahlung und -rückzahlung als eigene Datei.{' '}
        Stammdatenänderungen im Zeitraum: {masterDataChangeCount}.
      </p>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain bg-app-bg px-5 py-5 md:px-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiTile label="Brutto-Forecast" value={formatEuroInt(filteredBrutto)} />
            <KpiTile
              label="Mitarbeiter abzurechnen"
              value={String(employeeCountLabel)}
              unit={`/ ${candidates.length}`}
            />
            <KpiTile
              label="Offene Probleme"
              value={String(filteredIssues.length)}
              accentClassName={filteredIssues.length > 0 ? 'text-amber-600' : 'text-green-600'}
            />
          </div>

          {filteredIssues.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setIssuesOpen((v) => !v)}
                  className="flex items-center gap-2.5 text-left min-w-0 flex-1"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900">
                      Export blockiert - {filteredIssues.length}{' '}
                      {filteredIssues.length === 1 ? 'Problem' : 'Probleme'}
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      <b>{filteredIssues.length}</b> blockierend · müssen vor dem Export behoben werden
                    </p>
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 text-amber-800 shrink-0 transition-transform ${issuesOpen ? 'rotate-90' : ''}`}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => void runPreview()}
                  disabled={previewLoading}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-app-surface px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50 shrink-0"
                >
                  {previewLoading ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Erneut prüfen
                </button>
              </div>
              {issuesOpen && (
                <div className="app-scrollbar max-h-[380px] overflow-auto border-t border-amber-200 bg-app-surface">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead className="sticky top-0 bg-amber-50">
                      <tr>
                        <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-amber-900">
                          Mitarbeiter
                        </th>
                        <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-amber-900">
                          Datum
                        </th>
                        <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-amber-900">
                          Typ
                        </th>
                        <th scope="col" className="px-4 py-2.5 text-left text-xs font-semibold text-amber-900">
                          Problem
                        </th>
                        <th scope="col" className="px-4 py-2.5 text-right text-xs font-semibold text-amber-900">
                          Aktion
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleIssues.map((issue, idx) => {
                        const editable = canEditIssue(issue);
                        const emp = candidates.find((c) => c.id === issue.employeeId);
                        const av = emp ? avatarClass(emp.color_idx) : { bg: 'bg-app-surface-muted', text: 'text-app-text-muted' };
                        return (
                          <tr key={issueRowKey(issue, pageStart + idx)} className="border-b border-amber-50">
                            <td className="px-4 py-2.5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-bold ${av.bg} ${av.text}`}
                                >
                                  {initialsFromName(issue.employeeName || '?')}
                                </span>
                                <span className="font-medium text-app-text">{issue.employeeName || '-'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 tabular-nums text-app-text-muted whitespace-nowrap">
                              {formatIssueDate(issue.entryDate)}
                            </td>
                            <td className="px-4 py-2.5 text-app-text-muted whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                {issueTypeLabel(issue.entryType)}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-app-text-muted">{issue.message}</td>
                            <td className="px-4 py-2.5 text-right whitespace-nowrap">
                              {editable ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setEditTarget({
                                      employeeId: issue.employeeId,
                                      employeeName: issue.employeeName,
                                      entryDate: issue.entryDate,
                                      anchor: {
                                        top: r.top,
                                        left: r.left,
                                        width: r.width,
                                        height: r.height,
                                      },
                                    });
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 py-1 text-xs text-app-text-muted hover:bg-app-hover"
                                >
                                  <Pencil className="h-3 w-3" />
                                  Bearbeiten
                                </button>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {hiddenOnPage > 0 && (
                    <button
                      type="button"
                      onClick={() => setIssuesExpanded(true)}
                      className="w-full px-4 py-2.5 text-center text-xs font-medium text-amber-900 bg-amber-50 hover:bg-amber-100 border-t border-amber-100 transition-colors"
                    >
                      {hiddenOnPage} weitere {hiddenOnPage === 1 ? 'Problem' : 'Probleme'} anzeigen
                      <span className="text-amber-700 font-normal">
                        {' '}
                        (max. {ISSUES_PAGE_SIZE} pro Seite)
                      </span>
                    </button>
                  )}
                  {issuesPageCount > 1 && (
                    <div className="flex items-center justify-between gap-3 border-t border-amber-100 bg-amber-50/80 px-4 py-2">
                      <button
                        type="button"
                        onClick={() => goIssuesPage(safeIssuesPage - 1)}
                        disabled={safeIssuesPage <= 0}
                        className="rounded-md border border-amber-200 bg-app-surface px-2.5 py-1 text-xs font-medium text-amber-900 disabled:opacity-40 hover:bg-app-surface/90"
                      >
                        Zurück
                      </button>
                      <span className="text-xs text-amber-800 tabular-nums">
                        Seite {safeIssuesPage + 1} von {issuesPageCount}
                        <span className="text-amber-700 font-normal">
                          {' '}
                          · {filteredIssues.length} gesamt
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => goIssuesPage(safeIssuesPage + 1)}
                        disabled={safeIssuesPage >= issuesPageCount - 1}
                        className="rounded-md border border-amber-200 bg-app-surface px-2.5 py-1 text-xs font-medium text-amber-900 disabled:opacity-40 hover:bg-app-surface/90"
                      >
                        Weiter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-app-surface border border-app-border rounded-lg overflow-hidden min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-app-border">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-app-text">Lohnabrechnung je Mitarbeiter</p>
                <p className="text-xs text-app-text-subtle mt-0.5">
                  {sortedRows.length} von {visibleSummaries.length} · {formatPeriodMonthTitle(from)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-md border border-app-border bg-app-bg px-2.5 py-1">
                  <Search className="h-3.5 w-3.5 text-app-text-subtle shrink-0" />
                  <input
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    placeholder="Mitarbeiter durchsuchen"
                    className="w-[180px] border-0 bg-transparent text-xs text-app-text-muted outline-none placeholder:text-app-text-subtle"
                  />
                </div>
              </div>
            </div>

            {previewLoading && visibleSummaries.length === 0 ? (
              <InlineLoadingState label="Prüfe Daten…" className="py-12" />
            ) : candidatesLoading ? (
              <InlineLoadingState label="Lade Mitarbeiter…" className="py-12" />
            ) : sortedRows.length === 0 ? (
              <p className="py-12 text-center text-sm text-app-text-subtle">Keine Mitarbeiter in dieser Ansicht.</p>
            ) : (
              <div ref={setTableScrollNode} className="app-scrollbar max-h-[min(620px,50vh)] overflow-auto">
                <table className="w-full min-w-[1080px] text-sm">
                  <thead>
                    <tr>
                      {(
                        [
                          ['name', 'Mitarbeiter', 'left'],
                          ['role', 'Position', 'left'],
                          ['rate', 'Stundenlohn', 'right'],
                          ['reg', 'Arbeit', 'right'],
                          ['ot', 'Freizeitausgl.', 'right'],
                          ['vac', 'Urlaub', 'right'],
                          ['holiday', ENTRY_TYPE_LABEL.public_holiday, 'right'],
                          ['sick', 'Krank', 'right'],
                          ['zulage', 'Zulage', 'right'],
                          ['adjustments', 'Be- und Abzüge', 'right'],
                          ['brutto', 'Brutto-Forecast', 'right'],
                        ] as const
                      ).map(([key, label, align]) => (
                        <PayrollSortableHeader
                          key={key}
                          label={label}
                          active={sort.key === key}
                          dir={sort.dir}
                          align={align}
                          onSort={() => toggleSort(key)}
                        />
                      ))}
                      <th
                        scope="col"
                        className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-2.5 py-2.5 text-left text-xs font-medium text-app-text-muted"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 w-8 border-b border-app-border bg-app-surface-muted px-1 py-2.5"
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-app-border">
                    {sortedRows.map((emp) => {
                      const av = avatarClass(emp.color_idx);
                      const issueCount = issuesByEmployee.get(emp.id) ?? 0;
                      const isSelected = selectedRowId === emp.id;
                      const hourly = emp.hourly_wage;
                      return (
                        <tr
                          key={emp.id}
                          onClick={() => setSelectedRowId(emp.id)}
                          className={`cursor-pointer hover:bg-app-hover ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-2.5 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${av.bg} ${av.text}`}
                              >
                                {initialsFromName(emp.name)}
                              </span>
                              <span className="font-medium text-app-text max-w-[200px] truncate">{emp.name}</span>
                            </div>
                          </td>
                          <td className="px-2.5 py-2 text-app-text-muted whitespace-nowrap">{emp.position || '-'}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums text-app-text-muted whitespace-nowrap">
                            {hourly > 0
                              ? `${hourly.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}/h`
                              : '-'}
                          </td>
                          <td className="px-2.5 py-2 text-right tabular-nums text-app-text whitespace-nowrap">
                            {formatHoursWithUnit(emp.net_work_hours)}
                          </td>
                          <td
                            className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                              emp.hours_by_type.overtime_comp > 0 ? 'text-violet-700 font-medium' : 'text-app-text-subtle'
                            }`}
                            title="Reduziert die Arbeitsstunden"
                          >
                            {emp.hours_by_type.overtime_comp > 0
                              ? `−${formatHours(emp.hours_by_type.overtime_comp)} h`
                              : '-'}
                          </td>
                          <td
                            className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                              emp.hours_by_type.vacation > 0 ? 'text-app-text-muted' : 'text-app-text-subtle'
                            }`}
                          >
                            {formatHoursWithUnit(emp.hours_by_type.vacation)}
                          </td>
                          <td
                            className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                              emp.hours_by_type.public_holiday > 0 ? 'text-emerald-700' : 'text-app-text-subtle'
                            }`}
                          >
                            {formatHoursWithUnit(emp.hours_by_type.public_holiday)}
                          </td>
                          <td
                            className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                              emp.hours_by_type.sickness > 0 ? 'text-red-700 font-medium' : 'text-app-text-subtle'
                            }`}
                          >
                            {formatHoursWithUnit(emp.hours_by_type.sickness)}
                          </td>
                          <td className="px-2.5 py-2 text-right whitespace-nowrap">
                            <PayrollZulageAmount emp={emp} />
                          </td>
                          <td
                            className={`px-2.5 py-2 text-right tabular-nums whitespace-nowrap ${
                              emp.payroll_adjustment_net > 0
                                ? 'text-emerald-700'
                                : emp.payroll_adjustment_net < 0
                                  ? 'text-red-600'
                                  : 'text-app-text-subtle'
                            }`}
                          >
                            {formatSignedEuro(emp.payroll_adjustment_net)}
                          </td>
                          <td className="px-2.5 py-2 text-right whitespace-nowrap">
                            <PayrollBruttoAmount emp={emp} />
                          </td>
                          <td className="px-2.5 py-2 whitespace-nowrap">
                            {issueCount > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">
                                <AlertTriangle className="h-2.5 w-2.5" />
                                {issueCount} {issueCount === 1 ? 'Problem' : 'Probleme'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800">
                                <Check className="h-2.5 w-2.5" />
                                Bereit
                              </span>
                            )}
                          </td>
                          <td className="px-1 py-2 text-app-text-subtle">
                            <ChevronRight className="h-3.5 w-3.5 mx-auto" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-app-surface-muted text-xs font-semibold">
                      <td colSpan={3} className="px-2.5 py-3 text-app-text-subtle">
                        Summe ({sortedRows.length} Mitarbeiter)
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-app-text">
                        {formatHoursWithUnit(footTotals.reg)}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-violet-700">
                        {footTotals.ot > 0 ? `−${formatHours(footTotals.ot)} h` : '-'}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-app-text">
                        {formatHoursWithUnit(footTotals.vac)}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-emerald-700">
                        {formatHoursWithUnit(footTotals.holiday)}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-red-700">
                        {formatHoursWithUnit(footTotals.sick)}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-app-text-muted">
                        {formatEuro(footTotals.zulage)}
                      </td>
                      <td
                        className={`px-2.5 py-3 text-right tabular-nums ${
                          footTotals.adjustments > 0
                            ? 'text-emerald-700'
                            : footTotals.adjustments < 0
                              ? 'text-red-600'
                              : 'text-app-text-subtle'
                        }`}
                      >
                        {formatSignedEuro(footTotals.adjustments)}
                      </td>
                      <td className="px-2.5 py-3 text-right tabular-nums text-sm font-bold text-app-text">
                        {formatEuro(footTotals.brutto)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      {editTarget &&
        editPopoverLayout &&
        typeof document !== 'undefined' &&
        createPortal(
            <div
              ref={editPopoverRef}
              className="fixed z-[115] flex flex-col overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-xl"
              style={{
                left: editPopoverLayout.left,
                bottom: editPopoverLayout.bottom,
                width: editPopoverLayout.width,
                height: editPopoverLayout.maxHeight,
                maxHeight: editPopoverLayout.maxHeight,
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="payroll-entry-edit-title"
              onClick={(e) => e.stopPropagation()}
            >
              <span id="payroll-entry-edit-title" className="sr-only">
                Zeiteintrag bearbeiten
              </span>
              <EmployeeTimeEntryEditor
                key={`${editTarget.employeeId}-${editTarget.entryDate}`}
                employeeId={editTarget.employeeId}
                employeeName={editTarget.employeeName}
                entryDate={editTarget.entryDate}
                compact
                onSaved={() => {
                  onTimeEntrySaved?.();
                  setEditTarget(null);
                  void runPreview();
                }}
                onCancel={() => setEditTarget(null)}
                onError={(msg) => setError(msg)}
                onIntervalsChanged={() => {
                  onTimeEntrySaved?.();
                  void runPreview();
                }}
              />
            </div>,
          document.body,
        )}

      {selectedEmployee && !editTarget && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-slate-900/30"
          onClick={() => setSelectedRowId(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-[520px] h-full bg-app-surface shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Brutto-Forecast"
          >
            <div className="flex items-center gap-3 border-b border-app-border px-5 py-4">
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarClass(selectedEmployee.color_idx).bg} ${avatarClass(selectedEmployee.color_idx).text}`}
              >
                {initialsFromName(selectedEmployee.name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-app-text truncate">{selectedEmployee.name}</p>
                <p className="text-xs text-app-text-subtle truncate">
                  {selectedEmployee.position || '-'} · {formatPeriodMonthTitle(from)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRowId(null)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="border-b border-app-border bg-app-surface-muted px-5 py-4">
              <p className="text-xs font-medium text-app-text-muted">
                Brutto-Forecast · {formatPeriodLabel(from, to)}
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-app-text">
                {formatEuro(selectedEmployee.forecasted_gross_pay)}
              </p>
              <p className="mt-1 text-xs text-app-text-subtle">
                Abrechnungsstunden:{' '}
                <b className="text-app-text">{formatHoursWithUnit(selectedEmployee.billable_hours)}</b>
                {selectedEmployee.allowance_pay > 0 ? (
                  <>
                    {' '}
                    · Zulage <b className="text-app-text">{formatEuro(selectedEmployee.allowance_pay)}</b>
                  </>
                ) : null}
              </p>
            </div>

            <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-5 py-4">
              <p className="text-sm font-semibold text-app-text mb-3">Stunden im Zeitraum</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-app-border">
                    <th scope="col" className="pb-2 text-left text-xs font-medium text-app-text-muted">
                      Typ
                    </th>
                    <th scope="col" className="pb-2 text-right text-xs font-medium text-app-text-muted">
                      Stunden
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-app-border">
                    <td className="py-2.5 font-medium text-app-text">Arbeit (erfasst)</td>
                    <td className="py-2.5 text-right tabular-nums text-app-text-muted">
                      {formatHoursWithUnit(selectedEmployee.hours_by_type.work)}
                    </td>
                  </tr>
                  <tr className="border-b border-app-border">
                    <td className="py-2.5 font-medium text-app-text">{ENTRY_TYPE_LABEL.overtime_comp}</td>
                    <td className="py-2.5 text-right tabular-nums text-violet-700">
                      {selectedEmployee.hours_by_type.overtime_comp > 0
                        ? `−${formatHours(selectedEmployee.hours_by_type.overtime_comp)} h`
                        : '-'}
                    </td>
                  </tr>
                  <tr className="border-b border-app-border bg-app-surface-muted/80">
                    <td className="py-2.5 font-semibold text-app-text">Arbeit (netto)</td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-app-text">
                      {formatHoursWithUnit(selectedEmployee.net_work_hours)}
                    </td>
                  </tr>
                  {(
                    [
                      ['vacation', ENTRY_TYPE_LABEL.vacation],
                      ['sickness', ENTRY_TYPE_LABEL.sickness],
                      ['public_holiday', ENTRY_TYPE_LABEL.public_holiday],
                    ] as const
                  ).map(([key, label]) => (
                    <tr key={key} className="border-b border-app-border">
                      <td className="py-2.5 font-medium text-app-text">{label}</td>
                      <td className="py-2.5 text-right tabular-nums text-app-text-muted">
                        {formatHoursWithUnit(selectedEmployee.hours_by_type[key])}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-app-border bg-app-surface-muted/80">
                    <td className="py-2.5 font-semibold text-app-text">Abrechnungsstunden</td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-app-text">
                      {formatHoursWithUnit(selectedEmployee.billable_hours)}
                    </td>
                  </tr>
                  <tr className="border-b border-app-border">
                    <td className="py-2.5 text-app-text-muted">Lohn (Stunden × Satz)</td>
                    <td className="py-2.5 text-right tabular-nums text-app-text">
                      {formatEuro(selectedEmployee.wage_pay)}
                    </td>
                  </tr>
                  {(selectedEmployee.allowance_pay > 0 ||
                    selectedEmployee.allowance_hourly_pay > 0 ||
                    selectedEmployee.vma_pay > 0 ||
                    selectedEmployee.overnight_pay > 0) && (
                    <>
                      {selectedEmployee.allowance_hourly_pay > 0 && (
                        <tr className="border-b border-app-border">
                          <td className="py-2.5 text-app-text-muted">
                            Zulage/Stunde
                            {selectedEmployee.allowance_per_hour > 0
                              ? ` (${formatHours(selectedEmployee.allowance_hours)} h × ${selectedEmployee.allowance_per_hour.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}/h)`
                              : ''}
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-app-text">
                            {formatEuro(selectedEmployee.allowance_hourly_pay)}
                          </td>
                        </tr>
                      )}
                      {selectedEmployee.vma_pay > 0 && (
                        <tr className="border-b border-app-border">
                          <td className="py-2.5 text-app-text-muted">
                            VMA ({selectedEmployee.work_days} Tage ×{' '}
                            {selectedEmployee.vma_tax_free_per_day.toLocaleString('de-DE', {
                              style: 'currency',
                              currency: 'EUR',
                            })}
                            /Tag)
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-app-text">
                            {formatEuro(selectedEmployee.vma_pay)}
                          </td>
                        </tr>
                      )}
                      {selectedEmployee.overnight_pay > 0 && (
                        <tr className="border-b border-app-border">
                          <td className="py-2.5 text-app-text-muted">
                            ÜN-Pauschale ({selectedEmployee.work_days} Tage ×{' '}
                            {selectedEmployee.overnight_tax_free_per_day.toLocaleString('de-DE', {
                              style: 'currency',
                              currency: 'EUR',
                            })}
                            /Tag)
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-app-text">
                            {formatEuro(selectedEmployee.overnight_pay)}
                          </td>
                        </tr>
                      )}
                      <tr className="border-b border-app-border bg-app-surface-muted/80">
                        <td className="py-2.5 font-semibold text-app-text">Zulagen gesamt</td>
                        <td className="py-2.5 text-right font-semibold tabular-nums text-app-text">
                          {formatEuro(selectedEmployee.allowance_pay)}
                        </td>
                      </tr>
                    </>
                  )}
                  {selectedEmployee.payroll_adjustment_net !== 0 && (
                    <tr className="border-b border-app-border">
                      <td className="py-2.5 text-app-text-muted">Be- und Abzüge</td>
                      <td
                        className={`py-2.5 text-right tabular-nums ${
                          selectedEmployee.payroll_adjustment_net > 0
                            ? 'text-emerald-700'
                            : 'text-red-600'
                        }`}
                      >
                        {formatSignedEuro(selectedEmployee.payroll_adjustment_net)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="pt-3 font-bold text-app-text">Brutto gesamt</td>
                    <td className="pt-3 text-right font-bold tabular-nums text-app-text">
                      {formatEuro(selectedEmployee.forecasted_gross_pay)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {(issuesByEmployee.get(selectedEmployee.id) ?? 0) > 0 && (
                <div className="mt-5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>
                    <b>{issuesByEmployee.get(selectedEmployee.id)}</b> offene{' '}
                    {(issuesByEmployee.get(selectedEmployee.id) ?? 0) === 1 ? 'Problem' : 'Probleme'} blockieren den
                    Export für diesen Mitarbeiter.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-app-border px-5 py-3 flex gap-2">
              {onOpenEmployeeTimeTracking ? (
                <button
                  type="button"
                  onClick={() => {
                    onOpenEmployeeTimeTracking(selectedEmployee.id);
                    setSelectedRowId(null);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-app-border px-3 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                >
                  <Clock className="h-3.5 w-3.5" />
                  Zur Zeiterfassung
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
